import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, MessageCircle, Phone, MapPin } from 'lucide-react';
import { SITE } from '../seo/seoConfig';

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M16.5 3c.4 2.3 1.9 3.9 4.2 4.1v3.1c-1.6 0-3-.5-4.2-1.4v6.4a5.9 5.9 0 1 1-5.1-5.9v3.2a2.8 2.8 0 1 0 2 2.7V3h3.1z" /></svg>
);

const SOCIALS = [
  { key: 'facebook', label: 'LGRA on Facebook', href: SITE.socials.facebook, Icon: Facebook },
  { key: 'instagram', label: 'LGRA on Instagram', href: SITE.socials.instagram, Icon: Instagram },
  { key: 'tiktok', label: 'LGRA on TikTok', href: SITE.socials.tiktok, Icon: TikTokIcon },
  { key: 'youtube', label: 'LGRA on YouTube', href: SITE.socials.youtube, Icon: Youtube },
];

const COLUMNS = [
  { heading: 'Training', links: [
    ['Online Training Plans', '/online-training-plan'], ['Free Running Assessment', '/online-training-plan'], ['Running Classes', '/running-classes'],
    ['Kids & Teens Classes', '/running-classes/kids-teens'], ['Adult Classes', '/running-classes/adults'], ['Beginner Classes', '/running-classes/beginners'],
  ] },
  { heading: 'Coaching', links: [
    ['Private Coaching', '/coaching/private'], ['Running Technique', '/coaching/running-technique'], ['Race Preparation', '/coaching/race-preparation'],
    ['Workshops & Events', '/workshops'], ['Our Coaches', '/coaches'], ['Compare Options', '/compare-training-options'],
  ] },
  { heading: 'Explore', links: [
    ['Weekly Schedule', '/schedule'], ['Training Locations', '/#locations'], ['Race Calendar', '/race-calendar'], ['Runners’ Stories', '/runners-stories'],
    ['Runner’s Market', '/runner-market'], ['Membership & Packages', '/packages'], ['Booking Help', '/booking-help'],
  ] },
];

const POLICY_LINKS = [
  ['booking-rules', 'Booking Rules', '/policies/booking-rules'],
  ['cancellation-policy', 'Cancellation & Refund Policy', '/policies/cancellation-refund'],
  ['terms', 'Terms & Conditions', '/terms'],
  ['privacy', 'Privacy Policy', '/privacy'],
];

const linkCls = 'text-sm text-white/70 hover:text-white transition-colors';

const PublicFooter = () => (
  <footer className="bg-[#09090B] border-t border-white/10 mt-10" data-testid="public-footer">
    <div className="max-w-7xl mx-auto px-6 pt-12 pb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <img src="/lgra-logo.webp" alt="LGRA – Little Grappy Running Academy logo" width="44" height="44" loading="lazy" className="h-11 w-auto bg-white p-1 rounded" />
            <div>
              <div className="text-white font-black tracking-tight text-lg leading-tight">LGRA Running Academy</div>
              <div className="text-xs text-[#C4B5FD] tracking-wide font-semibold mt-0.5">Little Grappy Running Academy</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/60 max-w-sm leading-relaxed">Running coaching and training plans in Malaysia — coached classes in Kuala Lumpur, Shah Alam and Putrajaya, online plans anywhere.</p>
          <address className="not-italic mt-5 space-y-2 text-sm text-white/80" data-testid="footer-contact">
            <a href={`tel:${SITE.phone}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone size={14} className="text-[#C4B5FD]" /> {SITE.phoneDisplay}</a>
            <a href={SITE.whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-whatsapp"><MessageCircle size={14} className="text-[#25D366]" /> WhatsApp {SITE.whatsappDisplay}</a>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-[#C4B5FD]" /> Bukit Jalil, 57000 Kuala Lumpur, Malaysia</span>
          </address>
          <div className="flex items-center gap-2 mt-5" data-testid="footer-socials">
            {SOCIALS.map(({ key, label, href, Icon }) => (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/80 hover:bg-[#A855F7] hover:text-white hover:border-[#A855F7] transition-colors" data-testid={`footer-social-${key}`}><Icon size={16} /></a>
            ))}
          </div>
        </div>
        {COLUMNS.map((col) => (
          <nav key={col.heading} aria-label={`Footer ${col.heading}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-[#C4B5FD] mb-3">{col.heading}</div>
            <ul className="space-y-2">
              {col.links.map(([label, to]) => (
                <li key={label}>{to.startsWith('/#') ? <a href={to} className={linkCls}>{label}</a> : <Link to={to} className={linkCls} data-testid={`footer-link-${to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}>{label}</Link>}</li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="mt-10 border-t border-white/10 pt-5 flex flex-col items-center gap-2 text-center">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-white/60" data-testid="footer-policies">
          {POLICY_LINKS.map(([key, label, to], idx) => (
            <React.Fragment key={key}>
              <Link to={to} className="hover:text-white transition-colors" data-testid={`footer-${key}`}>{label}</Link>
              {idx < POLICY_LINKS.length - 1 && <span className="text-white/30" aria-hidden="true">·</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-white/50">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Little Grappy Running Academy (LGRA)</span>
          <span className="text-white/30" aria-hidden="true">·</span>
          <a href="https://wa.link/yuyuxf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-website-credit">Website by e-LGRA Solution</a>
        </div>
      </div>
    </div>
  </footer>
);

export default PublicFooter;
